import { useMemo, useState } from 'react';
import { AccountHistorySection } from './AccountHistorySection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { centsToDisplay } from '../../lib/money';
import {
  accountHasAssetsTab,
  accountTabLabel,
  resolveAccountTab,
  visibleViewTabIds,
  type AccountTabId,
} from './accountTabs';
import {
  escalationRoleLabel,
  escalationRolesForAccountType,
  normalizeContentCreationConfig,
  serviceLabel,
} from '../../lib/accountServices';
import { listContacts } from '../contacts';
import { computeDisplayName } from '../../types/contact';
import { useCurrentUser } from '../auth';
import type { Account, ContentCreationConfig, CommunityManagementConfig } from '../../types/account';
import type { FranchiseProfile } from '../../types/franchiseProfile';
import type { FranchiseLocationProfile } from '../../types/franchiseLocationProfile';
import {
  deleteAccount,
  getAccountDetail,
  getDeletePreview,
  listAccounts,
  listUsers,
  updateAccount,
  type AccountDetail as AccountDetailData,
} from './api';
import {
  buildAdAccountHolderCounts,
  classifyAdAccountSharing,
  describeSharedOn,
} from '../../lib/adAccountSharing';
import { accountManagerLabel } from '../../lib/accountManagerLabel';
import {
  getFranchiseLocationProfile,
  getFranchiseProfile,
} from '../franchise/api';
import { DeleteAccountModal } from './DeleteAccountModal';
import { ApiConnectionsSection } from './ApiConnectionsSection';
import { listSavedAudiences, audienceTargetingSummary } from '../metaAds';
import { CampaignList } from '../campaigns';
import { AccountContactsSection } from '../contacts';
import { PortalAccessSection } from '../clientPortal';
import { RolloutsForBrand } from '../rollouts';
import { AccountBackfillPanel } from '../reporting';
import { LeadRelaySummaryTile } from '../leadRelay';
import { AccountAssetsSection } from '../portalAssets';
// File-level import, NOT the kyckConnect barrel: that barrel exports pages which import this module's barrel, and a barrel-to-barrel
// cycle fails at module init rather than at compile. KyckConnectTab (and what it hosts) imports neither accounts nor a barrel.
import { KyckConnectTab } from '../kyckConnect/KyckConnectTab';
import { accountKyckConnectHref } from '../../lib/kyckConnect/links';
import { ClubwiseUploadSection } from '../clubwise';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import { pickAccountLinks } from '../../lib/accountLinks';
import { REPORT_TEMPLATE_LABELS } from '../../types/reportTemplate';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-4 py-1.5">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900">{value ?? <span className="text-slate-400">—</span>}</dd>
    </div>
  );
}

function UrlField({ label, url }: { label: string; url: string | null }) {
  return (
    <Field
      label={label}
      value={
        url ? (
          <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
            {url}
          </a>
        ) : (
          <span className="text-slate-400">—</span>
        )
      }
    />
  );
}

// Grouping primitive WITHIN a tab (the tabbed account page is the documented
// exception to the section-based-detail rule; sections still group content
// inside each tab). `id` is optional — retained only where a section historically
// carried one; it is no longer an anchor target now that the AnchorNav is gone.
function Section({
  id,
  title,
  children,
  actions,
}: {
  id?: string;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section id={id} className="bg-white rounded-md border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Breadcrumb({ ancestors, current }: { ancestors: Account[]; current: Account }) {
  if (ancestors.length === 0) return null;
  return (
    <nav className="text-sm text-slate-500 mb-2">
      {ancestors.map((a, i) => (
        <span key={a.id}>
          <Link to={`/accounts/${a.id}`} className="hover:underline">{a.name}</Link>
          <span className="mx-2 text-slate-300">/</span>
          {i === ancestors.length - 1 ? null : null}
        </span>
      ))}
      <span className="text-slate-700">{current.name}</span>
    </nav>
  );
}

// Read-only surface for an account's DEFAULT saved audiences (the per-audience
// "Account Default" flag, edited only on Meta Setup → Audiences). Lists the names +
// a one-line targeting summary, with a "Manage" deep-link that lands on Meta Setup
// with this account preselected and the Audiences tab active.
function DefaultAudiencesSectionBody({ accountId }: { accountId: string }) {
  const audiences = useQuery({
    queryKey: ['savedAudiences', accountId],
    queryFn: () => listSavedAudiences(accountId).then((r) => r.savedAudiences),
  });

  if (audiences.isLoading) {
    return <p className="text-sm text-slate-500">Loading…</p>;
  }
  if (audiences.error) {
    return <p className="text-sm text-slate-500">Couldn't load saved audiences.</p>;
  }

  const defaults = (audiences.data ?? []).filter((a) => a.isAccountDefault);
  if (defaults.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No account default audiences yet. Use Manage to mark which saved audiences are defaults.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
      {defaults.map((a) => (
        <li key={a.id} className="flex items-center justify-between gap-4 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-800">
              {a.name || 'Untitled audience'}
            </p>
            <p className="truncate text-xs text-slate-500">{audienceTargetingSummary(a)}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function FranchiseProfileSection({ profile }: { profile: FranchiseProfile | null }) {
  if (!profile) {
    return <p className="text-sm text-slate-500">No franchise profile attached.</p>;
  }
  return (
    <dl>
      <Field label="Club name prefix" value={profile.clubNamePrefix} />
      <Field label="Default Google path1" value={profile.defaultGooglePath1} />
      <Field
        label="Default Google headlines"
        value={
          profile.defaultGoogleBoilerplateHeadlines.length === 0 ? null : (
            <ul className="list-disc pl-5 space-y-0.5">
              {profile.defaultGoogleBoilerplateHeadlines.map((h, i) => (
                <li key={i} className="font-mono text-xs">{h}</li>
              ))}
            </ul>
          )
        }
      />
      <Field label="Landing template: offer" value={<code className="font-mono text-xs">{profile.landingPageTemplate.offer}</code>} />
      <Field label="Landing template: sale" value={<code className="font-mono text-xs">{profile.landingPageTemplate.sale}</code>} />
      <Field
        label="Landing template: location"
        value={profile.landingPageTemplate.location ? <code className="font-mono text-xs">{profile.landingPageTemplate.location}</code> : null}
      />
      <UrlField label="Ad copy doc" url={profile.defaultAdCopyDocUrl} />
      <UrlField label="Brand assets" url={profile.brandAssetsUrl} />
      <Field label="Brand portal notes" value={profile.brandPortalNotes && <pre className="whitespace-pre-wrap text-sm">{profile.brandPortalNotes}</pre>} />
      {/* ⚠️ The brand voice profile (2026-08-26). Shown here because it is
          authored on the form and view/edit parity is the rule — and because
          "what is the AI being told about this brand?" is a question an AM asks
          without wanting to open the editor. Legacy profiles predate the field
          and render the standard "—" via Field. */}
      <Field
        label="Brand voice profile (AI copy)"
        value={
          profile.brandVoiceProfile ? (
            <>
              <pre className="whitespace-pre-wrap text-sm">{profile.brandVoiceProfile}</pre>
              {/* ⚠️ PROVENANCE, so a profile drafted a year ago from six campaigns
                  reads as old rather than as silently authoritative. */}
              {profile.brandVoiceProfileDraftedAt && (
                <span className="mt-1 block text-xs text-slate-500">
                  AI-drafted from past copy on{' '}
                  {new Date(profile.brandVoiceProfileDraftedAt).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                  .
                </span>
              )}
            </>
          ) : null
        }
      />
      {/* ⚠️ Boundaries, never samples — see the form's own note. */}
      <Field
        label="Rejected copy (AI boundaries)"
        value={
          profile.brandCopyAntiExamples ? (
            <pre className="whitespace-pre-wrap text-sm">{profile.brandCopyAntiExamples}</pre>
          ) : null
        }
      />
      <Field
        label="Default Meta naming template"
        value={
          profile.defaultMetaCampaignNameTemplate ? (
            <code className="font-mono text-xs break-all">
              {profile.defaultMetaCampaignNameTemplate}
            </code>
          ) : null
        }
      />
      {/* Pre-P2b-3 polish round 5 §2.2: brand-default Google ad-label
          template, parallel to the Meta default above. Legacy
          FranchiseProfile docs may not carry this field yet — undefined
          renders as the standard "—" placeholder via Field. */}
      <Field
        label="Default Google ad label template"
        value={
          profile.defaultGoogleAdLabelTemplate ? (
            <code className="font-mono text-xs break-all">
              {profile.defaultGoogleAdLabelTemplate}
            </code>
          ) : null
        }
      />
    </dl>
  );
}

// Pre-P2b-3 polish round 2 (Item 3): brand-account toggle for
// "Directly shared with this Brand" vs "All Campaigns under this
// Brand (including inherited)". Default flips to Directly Shared so
// brand accounts behave the same as group/location accounts on first
// paint — AMs flip to All when they want the wider visibility-walk
// view. Local state only (no URL param, no persistence across page
// loads): per the spec the toggle is page-local, and an extra URL
// param would collide with the filter-bar URL params CampaignList
// already owns.
type BrandCampaignsMode = 'direct' | 'all';

function CampaignsForAccountSection({ account }: { account: Account }) {
  const [brandMode, setBrandMode] = useState<BrandCampaignsMode>('direct');

  if (account.accountType !== 'brand') {
    return (
      <Section
        id="campaigns"
        title="Campaigns directly shared with this account"
      >
        <CampaignList directlySharedWithAccountId={account.id} />
      </Section>
    );
  }

  const heading =
    brandMode === 'direct'
      ? 'Campaigns directly shared with this Brand'
      : 'All Campaigns under this Brand (including inherited)';

  return (
    <Section id="campaigns" title={heading}>
      <div className="mb-4 inline-flex rounded-md border border-slate-300 bg-slate-50 p-0.5 text-sm">
        <button
          type="button"
          onClick={() => setBrandMode('direct')}
          aria-pressed={brandMode === 'direct'}
          className={`px-3 py-1 rounded ${
            brandMode === 'direct'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Directly shared
        </button>
        <button
          type="button"
          onClick={() => setBrandMode('all')}
          aria-pressed={brandMode === 'all'}
          className={`px-3 py-1 rounded ${
            brandMode === 'all'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          All under Brand
        </button>
      </div>
      {brandMode === 'direct' ? (
        // Directly-shared mode reuses the same CampaignList path as
        // non-brand accounts — strict equality on
        // shareTargets[].accountId. Hides the Brand column (it's
        // always THIS brand) and the New Campaign button is also
        // suppressed in direct-shared mode by CampaignList.
        <CampaignList directlySharedWithAccountId={account.id} />
      ) : (
        // All-under-Brand mode preserves the existing visibility-walk
        // behaviour (every Campaign whose shareTargets walk up to or
        // are this brand). Brand column hidden; New Campaign button
        // visible with brand pre-fill.
        <CampaignList brandAccountId={account.id} />
      )}
    </Section>
  );
}

function FranchiseLocationProfileSection({ profile }: { profile: FranchiseLocationProfile | null }) {
  if (!profile) {
    return <p className="text-sm text-slate-500">No franchise location profile attached.</p>;
  }
  return (
    <dl>
      <Field label="Franchise suburb" value={profile.franchiseSuburb} />
      <Field label="Short suburb" value={profile.franchiseSuburbShort} />
      <Field label="Suburb slug" value={<code className="font-mono text-xs">{profile.franchiseSuburbSlug}</code>} />
      <Field label="State" value={profile.state} />
      <Field label="Package" value={profile.package} />
      <Field label="Default form type" value={profile.defaultFormType} />
      {/* Pre-P2b-3 polish round 5 §1: location-level Meta ad type default.
          Friendlier label than the raw enum string. */}
      <Field
        label="Default Meta ad type"
        value={
          profile.defaultMetaAdType === 'traffic'
            ? 'Website Lead'
            : profile.defaultMetaAdType === 'lead_gen'
              ? 'Meta Lead Gen'
              : <span className="text-slate-400 italic">No default</span>
        }
      />
      <UrlField label="Offer landing page" url={profile.offerLandingPage} />
      <UrlField label="Sale landing page" url={profile.saleLandingPage} />
      <UrlField label="Location landing page" url={profile.locationLandingPage} />
      <Field label="Club opened" value={profile.clubOpenedAt ? new Date(profile.clubOpenedAt).toLocaleDateString() : null} />
      <Field label="Gym info" value={profile.gymInfo && <pre className="whitespace-pre-wrap text-sm">{profile.gymInfo}</pre>} />
    </dl>
  );
}

// Phase 2b — read-only render of a channel-toggle set as a comma list (or —).
function channelSummary(channels: { facebook: boolean; instagram: boolean; google: boolean; tiktok: boolean }): ReactNode {
  const on = [
    channels.facebook && 'Facebook',
    channels.instagram && 'Instagram',
    channels.google && 'Google',
    channels.tiktok && 'TikTok',
  ].filter(Boolean);
  return on.length === 0 ? null : on.join(', ');
}

function ContentCreationView({
  config,
  currency,
}: {
  config: ContentCreationConfig;
  currency: import('../../types/account').Currency;
}) {
  const q = config.quotas;
  const showQuota = (n: number | null) => (typeof n === 'number' ? String(n) : null);
  // 2026-08-05 split — tolerate a not-yet-normalised doc (sales ?? legacy).
  const cc = normalizeContentCreationConfig(config);
  const block = (text: string) =>
    text.trim() ? <pre className="whitespace-pre-wrap text-sm font-sans">{text}</pre> : null;
  return (
    <dl>
      <Field label="Channels" value={channelSummary(config.channels)} />
      {config.channels.facebook && <Field label="Facebook Feed / month" value={showQuota(q.facebookFeed)} />}
      {config.channels.instagram && <Field label="Instagram Feed / month" value={showQuota(q.instagramFeed)} />}
      {(config.channels.facebook || config.channels.instagram) && (
        <Field label="Stories / month" value={showQuota(q.stories)} />
      )}
      {config.channels.google && <Field label="Google Business / month" value={showQuota(q.googleBusiness)} />}
      {config.channels.facebook && (
        <Field
          label="Facebook spend"
          value={
            config.mediaBudget.facebookSpendCents != null
              ? centsToDisplay(config.mediaBudget.facebookSpendCents, currency)
              : null
          }
        />
      )}
      {config.channels.instagram && (
        <Field
          label="Instagram spend"
          value={
            config.mediaBudget.instagramSpendCents != null
              ? centsToDisplay(config.mediaBudget.instagramSpendCents, currency)
              : null
          }
        />
      )}
      <Field label="Content sharing group" value={(config.contentSharingGroup ?? '').trim() || null} />
      <Field
        label="Sales contact details for post"
        value={block(cc.salesContactDetailsForPost)}
      />
      <Field
        label="Service contact details for post"
        value={block(cc.serviceContactDetailsForPost)}
      />
      <Field label="Notes" value={block(config.notes)} />
    </dl>
  );
}

// Dealership ads config view (meta_aia / google_vla): budget + feed URL.
function DealershipAdsView({
  config,
  currency,
}: {
  config: import('../../types/account').DealershipAdsConfig;
  currency: import('../../types/account').Currency;
}) {
  return (
    <dl>
      <Field
        label="Monthly budget"
        value={config.budgetCents != null ? centsToDisplay(config.budgetCents, currency) : null}
      />
      <UrlField label="Feed URL" url={config.feedUrl} />
    </dl>
  );
}

// Community-management config view. Resolves each escalation contactId to its
// contact name (a link); a dangling ref (contact deleted after linking) degrades
// gracefully to a muted "(contact <id> not found)" rather than crashing.
function CommunityManagementView({
  account,
  config,
}: {
  account: Account;
  config: CommunityManagementConfig;
}) {
  const contactsQuery = useQuery({
    queryKey: ['contacts', account.id],
    queryFn: () => listContacts(account.id),
  });
  const byId = new Map((contactsQuery.data ?? []).map((c) => [c.id, c]));
  const roles = escalationRolesForAccountType(account.accountType);
  return (
    <dl>
      <Field label="Channels managed" value={channelSummary(config.channels)} />
      <Field label="Sign-off person" value={config.signOffPerson.trim() || null} />
      {roles.map((role) => {
        const id = config.escalationContacts[role];
        let value: ReactNode = null;
        if (id) {
          const contact = byId.get(id);
          value = contact ? (
            <Link to={`/contacts/${contact.id}`} className="text-blue-600 hover:underline">
              {computeDisplayName(contact)}
            </Link>
          ) : (
            <span className="text-slate-400 italic">(contact {id} not found)</span>
          );
        }
        return <Field key={role} label={`Escalation — ${escalationRoleLabel(role)}`} value={value} />;
      })}
    </dl>
  );
}

export function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const detail = useQuery<AccountDetailData>({
    queryKey: ['account', id],
    queryFn: () => getAccountDetail(id!),
    enabled: !!id,
  });

  const brandProfile = useQuery({
    queryKey: ['franchiseProfile', id],
    queryFn: () => getFranchiseProfile(id!),
    enabled: !!id && detail.data?.account.accountType === 'brand',
  });
  const locationProfile = useQuery({
    queryKey: ['franchiseLocationProfile', id],
    queryFn: () => getFranchiseLocationProfile(id!),
    enabled: !!id && detail.data?.account.accountType === 'location',
  });
  // Resolve the AM uid -> display name for the Overview row. Only fetched when
  // an AM is actually assigned.
  const usersQuery = useQuery({
    // `includeUid` keeps a turned-off manager in the list so the name still resolves.
    queryKey: ['users', detail.data?.account.accountManagerId ?? null],
    queryFn: () => listUsers(undefined, detail.data?.account.accountManagerId ?? undefined),
    enabled: !!detail.data?.account.accountManagerId,
  });
  // Resolve the dealership's brand marque -> display name (only for a dealership
  // with a brand set).
  const brandMarque = useQuery({
    queryKey: ['account', detail.data?.account.brandAccountId],
    queryFn: () => getAccountDetail(detail.data!.account.brandAccountId!),
    enabled:
      detail.data?.account.accountType === 'dealership' &&
      !!detail.data?.account.brandAccountId,
  });

  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modal, setModal] = useState<null | 'cancel' | 'hard'>(null);

  // §3b — sharedness is roster-wide GROUND TRUTH (≥2 holders of one Meta ad
  // account OR one Google Ads customer), so it can't be read off this account
  // alone. Fetched ONLY while the Connections tab is open, since that is the only
  // place it renders and a fetch-all roster is not worth it on every page view.
  // Connections is visible for every account type and is never the default tab,
  // so the raw ?tab= param is exactly the right gate. Shares the ['accounts']
  // cache with AccountForm / the accounts list.
  const connectionsTabOpen = searchParams.get('tab') === 'connections';
  const rosterQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
    enabled: connectionsTabOpen,
  });
  const sharing = useMemo(
    () =>
      classifyAdAccountSharing(
        detail.data?.account ?? {},
        buildAdAccountHolderCounts(rosterQuery.data ?? []),
      ),
    [detail.data?.account, rosterQuery.data],
  );

  // Preview loads lazily when the operator opens the hard-delete modal; it
  // reports how many contacts would lose their accountLink.
  const preview = useQuery({
    queryKey: ['account', id, 'delete-preview'],
    queryFn: () => getDeletePreview(id!),
    enabled: !!id && modal === 'hard',
  });

  // Both mutation success paths close the modal FIRST, then do the async
  // invalidation / navigation. Waiting on invalidations before closing left
  // the modal open over the next render -- visible on the hard-delete path
  // because React Router keeps AccountDetail mounted across the :id change
  // (state survives the navigation) and visible on the cancel path because
  // the modal just sat there until the queries refetched. Conditional render
  // ({modal && <DeleteAccountModal />}) unmounts the modal, which resets the
  // typed "DELETE" input state for free.
  const cancelMut = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('missing id');
      await updateAccount(id, { status: 'cancelled' });
    },
    onSuccess: async () => {
      setModal(null);
      await qc.invalidateQueries({ queryKey: ['accounts'] });
      await qc.invalidateQueries({ queryKey: ['account', id] });
    },
  });

  const hardDeleteMut = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('missing id');
      return deleteAccount(id);
    },
    onSuccess: async (result) => {
      setModal(null);
      await qc.invalidateQueries({ queryKey: ['accounts'] });
      await qc.invalidateQueries({ queryKey: ['contacts'] });
      // Spec: redirect to the parent's detail page, or /accounts if it was a root.
      navigate(result.parentAccountId ? `/accounts/${result.parentAccountId}` : '/accounts');
    },
  });

  if (detail.isLoading) return <CenteredSpinner />;
  if (detail.error || !detail.data) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load account: {(detail.error as Error | undefined)?.message ?? 'not found'}
      </div>
    );
  }

  const { account, resolvedCurrency, ancestors, children } = detail.data;
  const addr = account.address;

  // Resolve AM uid -> name. Falls back to the raw uid (flagged) if the user is
  // gone or the list hasn't loaded yet, so the assignment is never hidden.
  const accountManagerDisplay = account.accountManagerId
    ? accountManagerLabel(account.accountManagerId, usersQuery.data ?? [])
    : '—';

  // Tab state persisted in the URL (?tab=…) so a deep link / refresh keeps the
  // tab, and so the Edit link can hand the current tab to the form.
  const visibleTabs = visibleViewTabIds(account.accountType);
  const activeTab = resolveAccountTab(searchParams.get('tab'), visibleTabs);
  const setTab = (id: AccountTabId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  return (
    // ⚠️ The Assets tab is a grid with a filter bar, so it uses the page's width; every other tab is a column of
    // label/value rows that read worse stretched. The cap came off the WHOLE page's root (max-w-4xl, 896px), which is why the
    // tab was a narrow card on a wide page. The header row keeps the cap so its Edit button does not jump between tabs.
    <div className={`space-y-4 ${activeTab === 'assets' || activeTab === 'kyckconnect' ? '' : 'max-w-4xl'}`}>
      <Breadcrumb ancestors={ancestors} current={account} />
      <div className={`flex items-center justify-between ${activeTab === 'assets' || activeTab === 'kyckconnect' ? 'max-w-4xl' : ''}`}>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">{account.name}</h1>
            {/* The demo club (KyckConnect demo data): a small staff-only marker. The portal shows no badge, so clients see a clean demo. */}
            {account.demoAccount === true && (
              <span className="rounded border border-slate-300 px-1.5 py-0.5 text-xs font-medium text-slate-600" data-testid="demo-account-badge">Demo</span>
            )}
            {/* External links (round 2 item 3). This page already holds the
                full Account, so it passes links from that rather than the
                shared read — no extra request, and fresh straight after an edit. */}
            <AccountLinkIcons links={pickAccountLinks(account)} />
          </div>
          <p className="text-sm text-slate-500 font-mono">{account.slug}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* The club's Business Profile and promotions (the fitness bot's only fact source). The page
              is open to any signed-in staff member, so the link is too; a per-account page has no
              place in the global nav. Operational accounts only: it describes a physical club. */}
          {accountHasAssetsTab(account.accountType) && (
            <Link to={accountKyckConnectHref(account.id, 'setup', 'profile')}>
              <Button variant="outline">Business Profile</Button>
            </Link>
          )}
          {user.role === 'admin' && account.accountType === 'brand' && (
            <Link to={`/accounts/${account.id}/import`}>
              <Button variant="outline">Import locations</Button>
            </Link>
          )}
          {user.role === 'admin' && (
            <Link to={`/accounts/${account.id}/edit?tab=${activeTab}`}>
              <Button variant="outline">Edit</Button>
            </Link>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setTab(v as AccountTabId)}>
        <TabsList className="flex-wrap h-auto">
          {visibleTabs.map((id) => (
            <TabsTrigger key={id} value={id}>
              {accountTabLabel(id)}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Details — identity + core account fields, address, notes, children,
            and (admin) the danger zone / account lifecycle. */}
        <TabsContent value="details" className="space-y-4">
          {/* Renders nothing unless this account (or a store beneath it, or a
              store of this brand) has a lead route. */}
          <LeadRelaySummaryTile accountId={account.id} />
          <Section title="Overview">
            <dl>
              <Field label="Account type" value={account.accountType} />
              <Field label="Industry" value={account.industry} />
              <Field label="Status" value={account.status} />
              <Field label="Currency" value={resolvedCurrency} />
              <Field label="Country" value={account.country} />
              <Field label="Account manager" value={accountManagerDisplay} />
              <Field label="Media spend paid by" value={account.mediaSpendPaidBy} />
              <Field label="Salesforce account id" value={account.salesforceAccountId} />
            </dl>
          </Section>

          <Section title="Address & Contact Details">
            <dl>
              <Field label="Business phone" value={account.phone} />
              {addr ? (
                <>
                  <Field label="Street" value={addr.street} />
                  <Field label="Suburb" value={addr.suburb} />
                  <Field label="State" value={addr.state} />
                  <Field label="Postcode" value={addr.postcode} />
                  <Field label="Country" value={addr.country} />
                </>
              ) : null}
            </dl>
            {!addr && <p className="mt-2 text-sm text-slate-500">No address on file.</p>}
          </Section>

          <Section title="Notes">
            <dl>
              <Field label="Description" value={account.description && <pre className="whitespace-pre-wrap text-sm font-sans">{account.description}</pre>} />
              <Field label="Targeting notes" value={account.targetingNotes && <pre className="whitespace-pre-wrap text-sm font-sans">{account.targetingNotes}</pre>} />
              <Field label="General notes" value={account.notes && <pre className="whitespace-pre-wrap text-sm font-sans">{account.notes}</pre>} />
              <Field label="Lead syncing notes" value={account.leadSyncingNotes && <pre className="whitespace-pre-wrap text-sm font-sans">{account.leadSyncingNotes}</pre>} />
            </dl>
          </Section>

          <Section title="Children">
            {children.length === 0 ? (
              <p className="text-sm text-slate-500">No child accounts.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {children.map((c) => (
                  <li key={c.id}>
                    <Link to={`/accounts/${c.id}`} className="text-blue-600 hover:underline">{c.name}</Link>
                    <span className="text-slate-400 ml-2">({c.accountType})</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {user.role === 'admin' && (
            <section className="rounded-md border-2 border-red-300 bg-red-50 p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-red-900">Danger zone</h2>
                <p className="text-sm text-red-900/80 mt-1">
                  Cancelling the account preserves all data and is reversible via the Edit form.
                  Permanent delete removes the account and cascades to the attached franchise
                  profiles and the contacts' linked-account lists. Writes an audit log entry with
                  the full snapshot of the deleted document.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {account.status !== 'cancelled' && (
                  <button
                    type="button"
                    onClick={() => setModal('cancel')}
                    className="inline-flex items-center justify-center rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                  >
                    Cancel account
                  </button>
                )}

                <div className="relative group inline-block">
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setModal('hard')}
                    disabled={children.length > 0}
                  >
                    Delete permanently
                  </Button>
                  {children.length > 0 && (
                    <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                      This account has {children.length} child{' '}
                      {children.length === 1 ? 'account' : 'accounts'}. Reparent or delete them first.
                    </span>
                  )}
                </div>
              </div>
            </section>
          )}
        </TabsContent>

        {/* Services — the services list + revealed per-service config. Phase 2a:
            the meta_ads / google_ads services show their master budget; the old
            standalone Run on Meta / Run on Google rows are gone (runsMeta /
            runsGoogle are now DERIVED from these services). */}
        <TabsContent value="services" className="space-y-4">
          <Section title="Services">
            <dl>
              <Field
                label="Services"
                value={
                  account.services.length === 0 ? null : (
                    <ul className="list-disc pl-5 space-y-0.5">
                      {account.services.map((s) => <li key={s}>{serviceLabel(s)}</li>)}
                    </ul>
                  )
                }
              />
              {account.services.includes('meta_ads') && (
                <Field
                  label="Meta master monthly budget"
                  value={centsToDisplay(account.metaMasterMonthlyBudget, resolvedCurrency)}
                />
              )}
              {account.services.includes('google_ads') && (
                <Field
                  label="Google master monthly budget"
                  value={centsToDisplay(account.googleMasterMonthlyBudget, resolvedCurrency)}
                />
              )}
            </dl>
          </Section>

          {/* Phase 2b — per-service config (shown only for active configurable
              services, mirroring the form's conditional reveal). */}
          {account.services.includes('content_creation') && account.serviceConfig?.contentCreation && (
            <Section title="Content Creation">
              <ContentCreationView
                config={account.serviceConfig.contentCreation}
                currency={resolvedCurrency}
              />
            </Section>
          )}
          {account.services.includes('community_management') && account.serviceConfig?.communityManagement && (
            <Section title="Community Management">
              <CommunityManagementView account={account} config={account.serviceConfig.communityManagement} />
            </Section>
          )}
          {account.services.includes('meta_aia') && account.serviceConfig?.metaAia && (
            <Section title="Meta AIA (Automated Inventory Ads)">
              <DealershipAdsView config={account.serviceConfig.metaAia} currency={resolvedCurrency} />
            </Section>
          )}
          {account.services.includes('google_vla') && account.serviceConfig?.googleVla && (
            <Section title="Google VLA (Vehicle Listing Ads)">
              <DealershipAdsView config={account.serviceConfig.googleVla} currency={resolvedCurrency} />
            </Section>
          )}
        </TabsContent>

        {/* Contacts — the linked-contacts sub-section (managed here). */}
        <TabsContent value="contacts" className="space-y-4">
          <Section title="Contacts">
            <AccountContactsSection accountId={account.id} />
          </Section>
          {/* Client Portal Phase 0. Lives on the Contacts tab because a portal
              user IS a contact who has been given sign-in access -- the two are
              the same people, and splitting them across tabs would invite
              inviting someone who is not on the account. */}
          <Section title="Client portal access">
            <PortalAccessSection account={account} />
          </Section>
        </TabsContent>

        {/* Connections — ad accounts, integration URLs, live API connections,
            reporting sync, and default audiences. */}
        <TabsContent value="connections" className="space-y-4">
          <Section title="Ad accounts & URLs">
            <dl>
              <Field label="Meta ad account id" value={account.fbAdAccountId} />
              <Field label="Meta verified advertiser ID" value={account.metaVerifiedAdvertiserId || <span className="text-slate-400">Not set</span>} />
              <UrlField label="Meta ad account URL" url={account.fbAdAccountUrl} />
              <Field label="Instagram account id" value={account.defaultInstagramAccountId} />
              <Field label="Instagram account name" value={account.defaultInstagramAccountName} />
              <Field
                label="Default conversion pixel"
                value={account.defaultPixelName ?? account.defaultPixelId ?? null}
              />
              <Field
                label="Default lead form"
                value={account.defaultLeadFormName ?? account.defaultLeadFormId ?? null}
              />
              <Field label="Google Ads customer id" value={account.googleAdsCustomerId} />
              <UrlField label="Google Ads URL" url={account.googleAdsUrl} />
              {/* The club's id in the client's own CRM — the join key behind the
                  leads / sales / conversion-rate panel on their report. */}
              <Field label="CRM ID" value={account.crmId} />
              <Field label="ClubWise code" value={account.clubwiseCode ?? null} />
            </dl>

            {/* §3b — "shared" is roster GROUND TRUTH on EITHER platform (Meta ad
                account or Google Ads customer), not just the stored flag; match
                tokens are one list serving both, so the amber gap note fires
                whichever side is shared. The flag alone still reads Yes (it is a
                deliberate pre-sharing marker), just without a platform.
                Its own sub-block BELOW both platforms' ad-account fields, mirroring
                AccountForm's placement so view and edit stay parallel — it used to
                sit among the Meta fields, which read as Meta-only. */}
            <div className="mt-4 border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold text-slate-900">
                Shared ad account attribution
              </h3>
              <dl className="mt-2">
                <Field
                  label="Shared ad account"
                  value={
                    sharing.sharedOn.length > 0 || account.isSharedAdAccount ? (
                      <>
                        Yes
                        {sharing.sharedOn.length > 0 && (
                          <span className="ml-2 text-xs text-slate-500">
                            on {describeSharedOn(sharing.sharedOn)}
                          </span>
                        )}
                        {(account.adAccountMatchTokens?.length ?? 0) === 0 && (
                          <span className="ml-2 text-xs italic text-amber-600">
                            no match tokens set — spend can't be attributed
                          </span>
                        )}
                      </>
                    ) : (
                      'No'
                    )
                  }
                />
                <Field
                  label="Match tokens"
                  value={
                    (account.adAccountMatchTokens?.length ?? 0) === 0 ? null : (
                      <ul className="flex flex-wrap gap-1.5">
                        {account.adAccountMatchTokens!.map((t) => (
                          <li key={t} className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">
                            {t}
                          </li>
                        ))}
                      </ul>
                    )
                  }
                />
              </dl>
            </div>

            <dl className="mt-4">
              <Field label="Google Analytics property id" value={account.googleAnalyticsPropertyId} />
              <Field
                label="GA4 property (connected)"
                value={
                  account.ga4PropertyId ? (
                    <span>
                      {account.ga4PropertyId}
                      {account.ga4ConnectedBy ? ` · connected by ${account.ga4ConnectedBy}` : ''}
                      {user.role === 'admin' && (
                        <>
                          {' · '}
                          <Link className="text-sky-700 underline" to="/ga4-connections">
                            Manage
                          </Link>
                        </>
                      )}
                    </span>
                  ) : user.role === 'admin' ? (
                    <Link className="text-sky-700 underline" to="/ga4-connections">
                      Not connected — open GA4 connections
                    </Link>
                  ) : null
                }
              />
              <UrlField label="Facebook page" url={account.facebookPageUrl} />
              <UrlField label="Instagram" url={account.instagramUrl} />
              <UrlField label="TikTok" url={account.tiktokUrl} />
              <UrlField label="Google Business Profile" url={account.googleBusinessProfileUrl} />
              <UrlField label="Website" url={account.websiteUrl} />
              <UrlField label="Report dashboard" url={account.reportUrl} />
              <Field
                label="Reporting sync"
                value={
                  account.reportingSyncEnabled
                    ? 'On (recurring Meta pull)'
                    : 'Off'
                }
              />
              <Field
                label="Client report template"
                value={
                  account.reportTemplate
                    ? `${REPORT_TEMPLATE_LABELS[account.reportTemplate]} (set on this account)`
                    : 'Inherited (the Edit page names where it comes from)'
                }
              />
              {account.accountType === 'brand' && (
                <Field
                  label="Client report: Meta goal split"
                  value={account.portalMetaGoalReport ? 'On (every location under this brand)' : 'Off'}
                />
              )}
              <UrlField label="Client Drive" url={account.clientDriveUrl} />
              <UrlField label="Internal Drive" url={account.internalDriveUrl} />
              <Field label="GTM container id" value={account.gtmContainerId} />
              <UrlField label="Tracking doc" url={account.trackingDocUrl} />
              <UrlField label="Zapier folder" url={account.zapierFolderUrl} />
            </dl>
            <ApiConnectionsSection accountId={account.id} />
          </Section>

          {/* ClubWise uploads (Anytime Fitness). The component renders NOTHING for
              an account whose brand does not read ClubWise uploads, so the section
              header is rendered only for accounts that do (the server decides). */}
          {account.accountType === 'location' && <ClubwiseUploadSection accountId={account.id} />}

          {/* Build B — on-demand Meta-history backfill (admin-only). */}
          {user.role === 'admin' && (
            <Section title="Reporting">
              <AccountBackfillPanel account={account} />
            </Section>
          )}

          <Section
            title="Default audiences"
            actions={
              <Link to={`/advertising/meta-setup?account=${account.id}&tab=audiences`}>
                <Button variant="outline" size="sm">
                  Manage
                </Button>
              </Link>
            }
          >
            <DefaultAudiencesSectionBody accountId={account.id} />
          </Section>
        </TabsContent>

        {/* Franchise — brand or location profile (type-scoped tab). */}
        {account.accountType === 'brand' && (
          <TabsContent value="franchise" className="space-y-4">
            <Section title="Franchise profile">
              {brandProfile.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : <FranchiseProfileSection profile={brandProfile.data ?? null} />}
            </Section>
          </TabsContent>
        )}
        {account.accountType === 'location' && (
          <TabsContent value="franchise" className="space-y-4">
            <Section title="Franchise location profile">
              {locationProfile.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : <FranchiseLocationProfileSection profile={locationProfile.data ?? null} />}
            </Section>
          </TabsContent>
        )}
        {/* Dealership — type-scoped tab (dealership accounts only). */}
        {account.accountType === 'dealership' && (
          <TabsContent value="dealership" className="space-y-4">
            <Section title="Dealership">
              <dl>
                <div className="grid grid-cols-3 gap-2 py-1 text-sm">
                  <dt className="text-slate-500">Brand (marque)</dt>
                  <dd className="col-span-2">
                    {account.brandAccountId ? (
                      <Link
                        to={`/accounts/${account.brandAccountId}`}
                        className="text-blue-600 hover:underline"
                      >
                        {brandMarque.data?.account.name ?? account.brandAccountId}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </dd>
                </div>
                <Field label="Website platform" value={account.dealershipWebsitePlatform} />
                <Field label="CRM system" value={account.dealershipCrmSystem} />
              </dl>
            </Section>
          </TabsContent>
        )}

        {/* Campaigns — campaigns/rollouts visible to this account. */}
        <TabsContent value="campaigns" className="space-y-4">
          <CampaignsForAccountSection account={account} />
          {account.accountType === 'brand' && (
            <Section title="Rollouts">
              <RolloutsForBrand brandAccountId={account.id} />
            </Section>
          )}
        </TabsContent>

        {/* Assets — client-portal photos and videos, reviewed by Kyck (commit 13).
            Operational accounts only (accountHasAssetsTab). */}
        {visibleTabs.includes('assets') && (
          <TabsContent value="assets" className="space-y-4">
            <Section title="Client portal assets">
              <AccountAssetsSection accountId={account.id} />
            </Section>
          </TabsContent>
        )}

        {/* KyckConnect — this club's lead dashboard, leads, calls and setup checklist (redesign Phase 1; it REPLACED the Leads
            tab). Operational accounts only. */}
        {visibleTabs.includes('kyckconnect') && (
          <TabsContent value="kyckconnect" className="space-y-4">
            <Section title="KyckConnect">
              <KyckConnectTab accountId={account.id} />
            </Section>
          </TabsContent>
        )}

        {/* Activity — audit / history timeline. */}
        <TabsContent value="activity" className="space-y-4">
          <Section title="History">
            <AccountHistorySection accountId={account.id} currency={account.currency} />
          </Section>
        </TabsContent>
      </Tabs>

      {modal && (
        <DeleteAccountModal
          mode={modal}
          account={account}
          childrenCount={children.length}
          contactsLinked={preview.data?.contactsLinked ?? 0}
          onClose={() => setModal(null)}
          onConfirmCancel={() => cancelMut.mutateAsync()}
          onConfirmHard={() => hardDeleteMut.mutateAsync().then(() => undefined)}
        />
      )}
    </div>
  );
}
